// NOTE:This component is almost identical to the other DatePickerContent, aside from using input-v2 and some style adjustments
// These components should be kept in sync until SingleDatePicker and DatePickerContent are removed
'use client'

import dayjs, { Dayjs } from 'dayjs'
import { CalendarIcon } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Calendar } from '@/components/ui/calendar'
import { Input } from '@/components/ui/input-v2'
import { cn } from '@/lib/utils'
import { useTypedTranslation } from '~/common/hooks/useTypedTranslation'
import { TimePicker } from '../time-picker/time-picker'

type DatePickerContentProps = {
  disabled?: boolean
  error: string | null
  inputValue: string
  locale: string
  dateFormat: string
  datePattern: string
  customPattern: string | null
  currentMonth: Dayjs
  selected?: Date
  value?: Dayjs | 'now'
  withTime?: boolean
  showTodayButton?: boolean
  isDynamicNow: boolean
  onInputChange: (value: string) => void
  onKeyDown: (e: React.KeyboardEvent<HTMLInputElement>) => void
  onMonthChange: (date: Date) => void
  onDateChange: (date?: Dayjs) => void
  onTimeChange: (type: 'hour' | 'minute', value: number) => void
  onTodayClick: () => void
  onResetClick?: () => void
  onDoneClick: () => void
}

export function DatePickerContent({
  disabled,
  error,
  inputValue,
  locale,
  dateFormat,
  datePattern,
  customPattern,
  currentMonth,
  selected,
  value,
  withTime,
  showTodayButton,
  isDynamicNow,
  onInputChange,
  onKeyDown,
  onMonthChange,
  onDateChange,
  onTimeChange,
  onTodayClick,
  onResetClick,
  onDoneClick,
}: DatePickerContentProps) {
  const { t } = useTypedTranslation()

  return (
    <div className="flex flex-col gap-3 pt-4">
      <div className="flex flex-col gap-1 px-4">
        <div className="relative">
          <Input
            className={cn('rounded border border-gray-4 text-xs', error && 'border-red-5')}
            disabled={disabled}
            lang={locale}
            onChange={(e) => onInputChange(e.target.value)}
            onKeyDown={onKeyDown}
            pattern={datePattern}
            placeholder={dateFormat}
            type="text"
            value={inputValue}
          />
          <CalendarIcon className="absolute end-3 top-1/2 h-3.5 w-3.5 flex-shrink-0 -translate-y-1/2 transform text-gray-10" />
        </div>
        {error && (
          <div className="max-w-[220px]">
            {error && <span className="break-words text-xs text-red-9">{error}</span>}
          </div>
        )}
      </div>
      {customPattern && !error && (
        <label className="break-words px-4">
          {t('date_picker.custom_date_format', { format: customPattern })}
        </label>
      )}
      {withTime && value !== 'now' && (
        <div className="px-4">
          <TimePicker
            fullWidth
            hour={value?.hour() ?? 0}
            minute={value?.minute() ?? 0}
            onHourChange={(value) => onTimeChange('hour', value)}
            onMinuteChange={(value) => onTimeChange('minute', value)}
          />
        </div>
      )}
      <div className="flex flex-row items-start border-y border-gray-4">
        <Calendar
          className="px-4"
          initialFocus
          mode="single"
          month={currentMonth.toDate()}
          onMonthChange={onMonthChange}
          onSelect={(date) => onDateChange(date ? dayjs(date) : undefined)}
          selected={selected}
        />
      </div>

      <div className="flex items-center justify-between p-4 pt-0">
        {showTodayButton && (
          <div>
            <Button
              className={cn('rounded', isDynamicNow && 'border border-gray-10 bg-gray-2')}
              disabled={!!error || disabled}
              onClick={onTodayClick}
              size="xs"
              type="button"
              variant="outline"
            >
              {t('date_picker.today')}
            </Button>
          </div>
        )}
        <div className="flex flex-1 justify-end gap-2">
          {onResetClick && (
            <Button
              className="rounded"
              disabled={!!error || disabled}
              onClick={onResetClick}
              size="xs"
              type="button"
              variant="ghost"
            >
              {t('date_picker.reset')}
            </Button>
          )}
          <Button
            className="rounded"
            disabled={!!error || disabled}
            onClick={onDoneClick}
            size="xs"
            type="button"
            variant="primary"
          >
            {t('date_picker.done')}
          </Button>
        </div>
      </div>
    </div>
  )
}
